import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PaperPlaneTilt, LinkSimple, ArrowClockwise, Warning } from "@phosphor-icons/react";

/**
 * Making Tax Digital (MTD) VAT tab.
 * - Visible when the active company has a `vat_number`.
 * - Connect / Disconnect to HMRC (OAuth2 authorization code)
 * - List obligations (fetched from HMRC or shown as manual periods)
 * - Compute VAT Boxes 1-9 from ledger and submit
 */
export default function MTDPage() {
  const { activeCompany, formatApiError } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [obligationsError, setObligationsError] = useState(null);
  const [tab, setTab] = useState("prepare");
  const [range, setRange] = useState({
    from_date: firstOfMonth(-3),
    to_date: today(),
    period_key: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  function firstOfMonth(offset = 0) {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  }
  function today() {
    return new Date().toISOString().slice(0, 10);
  }

  // Detect callback flag from HMRC
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("connected") === "1") toast.success("HMRC connected");
    if (qs.get("error")) toast.error(`HMRC: ${qs.get("error")}`);
  }, [location.search]);

  const loadAll = async () => {
    if (!activeCompany) return;
    try {
      const [c, s, a] = await Promise.all([
        api.get(`/mtd/config`),
        api.get(`/companies/${activeCompany.id}/mtd/status`),
        api.get(`/companies/${activeCompany.id}/accounts`),
      ]);
      setConfig(c.data);
      setStatus(s.data);
      setAccounts(a.data);
      if (s.data.connected) {
        try {
          const o = await api.get(`/companies/${activeCompany.id}/mtd/obligations?status=O`);
          setObligations(o.data.obligations || []);
          setObligationsError(null);
        } catch (e) {
          setObligations([]);
          setObligationsError(formatApiError(e.response?.data?.detail) || e.message);
        }
      }
      const subs = await api.get(`/companies/${activeCompany.id}/mtd/submissions`);
      setSubmissions(subs.data);
    } catch (e) {
      // company probably has no vat number
    }
  };

  const saveSettings = async (updates) => {
    setSavingSettings(true);
    try {
      await api.patch(`/companies/${activeCompany.id}`, updates);
      toast.success("VAT settings saved");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const connect = async () => {
    try {
      const origin = window.location.origin;
      const { data } = await api.get(
        `/companies/${activeCompany.id}/mtd/authorize?origin=${encodeURIComponent(origin)}`
      );
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect from HMRC?")) return;
    try {
      await api.post(`/companies/${activeCompany.id}/mtd/disconnect`);
      toast.success("HMRC disconnected");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/mtd/vat-return/preview`,
        range
      );
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyObligation = (ob) => {
    setRange({
      from_date: ob.start,
      to_date: ob.end,
      period_key: ob.periodKey,
    });
    setTab("prepare");
    setTimeout(runPreview, 0);
  };

  const collectClientHeaders = () => {
    // HMRC Fraud Prevention: browser-collected values (best-effort)
    const nav = window.navigator || {};
    const scr = window.screen || {};
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const now = new Date();
    const tzOff = -now.getTimezoneOffset();
    const sign = tzOff >= 0 ? "+" : "-";
    const abs = Math.abs(tzOff);
    const hh = String(Math.floor(abs / 60)).padStart(2, "0");
    const mm = String(abs % 60).padStart(2, "0");
    return {
      "Gov-Client-Browser-JS-User-Agent": nav.userAgent || "",
      "Gov-Client-Timezone": `UTC${sign}${hh}:${mm}`,
      "Gov-Client-Screens": `width=${scr.width || 0}&height=${scr.height || 0}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${scr.colorDepth || 24}`,
      "Gov-Client-Window-Size": `width=${window.innerWidth}&height=${window.innerHeight}`,
      "Gov-Client-Browser-Plugins": "",
      "Gov-Client-Browser-Do-Not-Track": nav.doNotTrack === "1" ? "true" : "false",
      "Gov-Client-Local-IPs": "",
    };
  };

  const submitReturn = async () => {
    if (!range.period_key) return toast.error("Choose a period first");
    if (!preview) return toast.error("Run a preview first");
    setSubmitting(true);
    try {
      const client_headers = collectClientHeaders();
      const { data } = await api.post(
        `/companies/${activeCompany.id}/mtd/vat-return/submit`,
        {
          period_key: range.period_key,
          from_date: range.from_date,
          to_date: range.to_date,
          finalised: true,
          client_headers,
        }
      );
      toast.success(`Submitted. HMRC receipt: ${data.receipt?.formBundleNumber || data.receipt?.processingDate || "OK"}`);
      if (data.auto_journal?.id) {
        toast.success(`Auto-journal posted on ${data.auto_journal.date} · ${data.auto_journal.direction} · Journal ${data.auto_journal.id.slice(0, 8)}`);
      } else if (data.auto_journal?.error) {
        toast.warning(`Auto-journal skipped: ${data.auto_journal.error}`);
      }
      setConfirmSubmit(false);
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? JSON.stringify(detail) : (detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const eligibleMsg = useMemo(() => {
    if (!status) return null;
    if (!status.eligible) return "This company has no VAT number. Add one on the Companies page to enable MTD.";
    if (!status.server_configured) return "HMRC OAuth is not configured on the server. Ask an admin to set HMRC_CLIENT_ID / SECRET / REDIRECT_URI in the backend .env.";
    return null;
  }, [status]);

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const currency = activeCompany.currency || "GBP";

  return (
    <>
      <PageHeader
        eyebrow="UK HMRC"
        title="Making Tax Digital — VAT"
        description="Prepare and submit VAT returns directly to HMRC via the MTD API. Fully digital audit trail."
        actions={
          <div className="flex gap-2">
            {status?.connected ? (
              <Button
                variant="outline"
                onClick={disconnect}
                data-testid="mtd-disconnect-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <LinkSimple size={14} className="mr-1" /> Disconnect HMRC
              </Button>
            ) : (
              <Button
                onClick={connect}
                disabled={!status?.eligible || !status?.server_configured}
                data-testid="mtd-connect-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <LinkSimple size={14} className="mr-1" /> Connect to HMRC
              </Button>
            )}
          </div>
        }
      />
      <PageBody>
        {eligibleMsg && (
          <div className="border border-destructive/40 bg-destructive/5 p-4 text-sm flex items-start gap-2" data-testid="mtd-warning">
            <Warning size={16} className="text-destructive mt-0.5" />
            <span>{eligibleMsg}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="VRN" value={status?.vat_number || "—"} testId="mtd-vrn" />
          <StatCard label="Environment" value={config?.env || "—"} testId="mtd-env" />
          <StatCard
            label="Status"
            value={status?.connected ? "Connected" : "Not connected"}
            testId="mtd-connected"
          />
          <StatCard
            label="Token expires"
            value={status?.expires_at ? new Date(status.expires_at).toLocaleString() : "—"}
            testId="mtd-expires"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="prepare" data-testid="mtd-tab-prepare" className="font-mono uppercase text-xs tracking-wider">Prepare Return</TabsTrigger>
            <TabsTrigger value="obligations" data-testid="mtd-tab-obligations" className="font-mono uppercase text-xs tracking-wider">Obligations</TabsTrigger>
            <TabsTrigger value="history" data-testid="mtd-tab-history" className="font-mono uppercase text-xs tracking-wider">Submissions</TabsTrigger>
            <TabsTrigger value="settings" data-testid="mtd-tab-settings" className="font-mono uppercase text-xs tracking-wider">Settings</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "prepare" && (
          <div className="space-y-4">
            <div className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <Label className="text-xs uppercase tracking-widest">From</Label>
                <Input type="date" value={range.from_date} onChange={(e) => setRange({ ...range, from_date: e.target.value })} data-testid="mtd-from" className="rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">To</Label>
                <Input type="date" value={range.to_date} onChange={(e) => setRange({ ...range, to_date: e.target.value })} data-testid="mtd-to" className="rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Period Key</Label>
                <Input
                  value={range.period_key}
                  onChange={(e) => setRange({ ...range, period_key: e.target.value })}
                  placeholder="e.g. 26A1 (from HMRC)"
                  data-testid="mtd-period-key"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={runPreview}
                  disabled={previewLoading}
                  data-testid="mtd-preview-btn"
                  className="rounded-sm font-mono text-xs uppercase tracking-wider"
                >
                  <ArrowClockwise size={14} className="mr-1" /> {previewLoading ? "Calculating..." : "Preview"}
                </Button>
                <Button
                  onClick={() => setConfirmSubmit(true)}
                  disabled={!preview || !status?.connected || !range.period_key}
                  variant="default"
                  data-testid="mtd-submit-btn"
                  className="rounded-sm font-mono text-xs uppercase tracking-wider"
                >
                  <PaperPlaneTilt size={14} className="mr-1" /> Submit to HMRC
                </Button>
              </div>
            </div>

            {preview && (
              <div className="border border-border bg-card">
                <div className="border-b border-border p-4 flex justify-between items-baseline">
                  <div>
                    <div className="overline text-muted-foreground">VAT Return Preview</div>
                    <div className="text-lg font-serif-display">
                      {preview.from_date} → {preview.to_date}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="overline text-muted-foreground">Net VAT Due (Box 5)</div>
                    <div className="text-3xl font-serif-display text-primary num">
                      {fmtMoney(preview.netVatDue, currency)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <BoxRow n={1} label="VAT due on sales & other outputs" value={preview.vatDueSales} currency={currency} />
                  <BoxRow n={2} label="VAT due on EC acquisitions" value={preview.vatDueAcquisitions} currency={currency} />
                  <BoxRow n={3} label="Total VAT due (Box 1 + 2)" value={preview.totalVatDue} currency={currency} bold />
                  <BoxRow n={4} label="VAT reclaimed on purchases" value={preview.vatReclaimedCurrPeriod} currency={currency} />
                  <BoxRow n={5} label="Net VAT to pay / reclaim" value={preview.netVatDue} currency={currency} bold />
                  <BoxRow n={6} label="Total value of sales (excl. VAT)" value={preview.totalValueSalesExVAT} currency={currency} />
                  <BoxRow n={7} label="Total value of purchases (excl. VAT)" value={preview.totalValuePurchasesExVAT} currency={currency} />
                  <BoxRow n={8} label="Total value EC supplies (excl. VAT)" value={preview.totalValueGoodsSuppliedExVAT} currency={currency} />
                  <BoxRow n={9} label="Total value EC acquisitions (excl. VAT)" value={preview.totalAcquisitionsExVAT} currency={currency} />
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "obligations" && (
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40">
                <tr className="text-left">
                  <Th>Period Key</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Due</Th>
                  <Th>Status</Th>
                  <Th className="text-right pr-4">Action</Th>
                </tr>
              </thead>
              <tbody>
                {obligations.map((ob) => (
                  <tr key={ob.periodKey} data-testid={`obligation-${ob.periodKey}`} className="border-b border-border last:border-b-0">
                    <Td className="font-mono">{ob.periodKey}</Td>
                    <Td className="num">{ob.start}</Td>
                    <Td className="num">{ob.end}</Td>
                    <Td className="num">{ob.due}</Td>
                    <Td>
                      <span className="tag-badge">{ob.status === "O" ? "Open" : "Fulfilled"}</span>
                    </Td>
                    <Td className="text-right pr-4">
                      <Button size="sm" variant="outline" onClick={() => applyObligation(ob)} data-testid={`use-period-${ob.periodKey}`} className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider">
                        Use this period
                      </Button>
                    </Td>
                  </tr>
                ))}
                {obligations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                      {obligationsError
                        ? `Unable to load obligations from HMRC: ${obligationsError}`
                        : status?.connected
                          ? "No open obligations found."
                          : "Connect to HMRC to fetch obligations."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "history" && (
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40">
                <tr className="text-left">
                  <Th>Submitted</Th>
                  <Th>Period Key</Th>
                  <Th>Range</Th>
                  <Th className="text-right">Net VAT</Th>
                  <Th>Status</Th>
                  <Th>Auto-Journal</Th>
                  <Th>Receipt</Th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} data-testid={`submission-${s.id}`} className="border-b border-border last:border-b-0">
                    <Td className="num">{(s.submitted_at || "").slice(0, 19).replace("T", " ")}</Td>
                    <Td className="font-mono">{s.period_key}</Td>
                    <Td className="num">{s.from_date} → {s.to_date}</Td>
                    <Td className="text-right num">{fmtMoney(s.payload?.netVatDue || 0, currency)}</Td>
                    <Td>
                      <span className={`tag-badge ${s.success ? "text-primary" : "text-destructive"}`}>
                        {s.success ? "Submitted" : "Failed"}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs">
                      {s.auto_journal_id ? (
                        <span className="text-primary">Posted {s.due_date ? `· ${s.due_date}` : ""}</span>
                      ) : "—"}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {s.response?.formBundleNumber || s.response?.processingDate || "—"}
                    </Td>
                  </tr>
                ))}
                {submissions.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">No submissions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "settings" && (
          <div className="border border-border bg-card p-6 space-y-6 max-w-3xl">
            <div>
              <div className="overline text-muted-foreground mb-1">Auto-Journal on Submission</div>
              <p className="text-sm text-muted-foreground mb-3">
                When enabled, after each successful HMRC submission LedgerLoom auto-posts a payment (or refund) journal on the due date — Dr VAT Liability / Cr Bank — so your ledger reconciles the moment HMRC accepts your return.
              </p>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="mtd-auto-toggle">
                <input
                  type="checkbox"
                  checked={!!status?.vat_auto_journal}
                  onChange={(e) => saveSettings({ vat_auto_journal: e.target.checked })}
                  disabled={savingSettings}
                  className="h-4 w-4"
                />
                <span className="text-sm">Auto-post payment journal on the due date</span>
              </label>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">Default VAT Bank Account</Label>
              <Select
                value={status?.vat_bank_account_id || ""}
                onValueChange={(v) => saveSettings({ vat_bank_account_id: v })}
              >
                <SelectTrigger data-testid="mtd-bank-select" className="rounded-sm font-mono max-w-md">
                  <SelectValue placeholder="Select bank account HMRC is paid from" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => a.type === "asset").map((a) => (
                    <SelectItem key={a.id} value={a.id} className="font-mono">
                      {a.code} · {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">
                Used for the credit side of the auto-journal when HMRC is paid.
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">VAT Liability Account (optional override)</Label>
              <Select
                value={status?.vat_liability_account_id || ""}
                onValueChange={(v) => saveSettings({ vat_liability_account_id: v })}
              >
                <SelectTrigger data-testid="mtd-liab-select" className="rounded-sm font-mono max-w-md">
                  <SelectValue placeholder="Default: Sales Tax Payable (2100)" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => a.type === "liability").map((a) => (
                    <SelectItem key={a.id} value={a.id} className="font-mono">
                      {a.code} · {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">
                Debit side of the payment journal. Leaves the default alone unless you use a custom VAT account.
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="overline text-muted-foreground">Preview of next journal</div>
              <div className="mt-2 grid grid-cols-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Date</div>
                  <div className="num">Period end + 1 month + 7 days</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Debit</div>
                  <div className="font-mono">VAT Liability (Box 5)</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Credit</div>
                  <div className="font-mono">Bank Account (Box 5)</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Dialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Confirm HMRC Submission</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="border border-border p-4 space-y-1">
                <div><span className="overline mr-2">VRN</span><span className="num">{status?.vat_number}</span></div>
                <div><span className="overline mr-2">Period</span><span className="num">{range.period_key} · {range.from_date} → {range.to_date}</span></div>
                <div><span className="overline mr-2">Net VAT</span><span className="num">{fmtMoney(preview?.netVatDue || 0, currency)}</span></div>
              </div>
              <p className="text-xs text-muted-foreground">
                By submitting you declare the information is true and complete. This return will be marked as <strong>finalised</strong> and cannot be edited after submission.
              </p>
              {status?.vat_auto_journal && status?.vat_bank_account_id && (
                <div className="border border-primary/30 bg-primary/5 p-3 text-xs">
                  <strong>Auto-journal ON:</strong> A payment journal (Dr VAT Liability / Cr Bank) for {fmtMoney(preview?.netVatDue || 0, currency)} will be posted automatically on the due date (period end + 1 month + 7 days).
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={submitReturn}
                disabled={submitting}
                data-testid="mtd-submit-confirm"
                className="rounded-sm font-mono uppercase text-xs tracking-wider"
              >
                {submitting ? "Submitting..." : "Yes, Submit to HMRC"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
const StatCard = ({ label, value, testId }) => (
  <div className="border border-border bg-card p-4" data-testid={testId}>
    <div className="overline text-muted-foreground">{label}</div>
    <div className="text-lg font-serif-display mt-1 num truncate">{value}</div>
  </div>
);
const BoxRow = ({ n, label, value, currency, bold }) => (
  <div className={`p-4 border-b border-border md:border-b md:[&:nth-last-child(-n+2)]:border-b-0 flex justify-between items-center ${bold ? "bg-muted/40" : ""}`} data-testid={`vat-box-${n}`}>
    <div>
      <div className="overline text-muted-foreground">Box {n}</div>
      <div className={`text-sm ${bold ? "font-semibold" : ""}`}>{label}</div>
    </div>
    <div className={`num text-lg ${bold ? "font-semibold text-primary" : ""}`}>
      {typeof value === "number" && !Number.isInteger(value) ? fmtMoney(value, currency) : (value ?? 0).toLocaleString()}
    </div>
  </div>
);
